import { BaseSyntheticEvent, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button } from '@iwoca/orion';
import { Form, Formik, FormikErrors } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import styles from './EmailInput.module.css';
import { fetchPostCustomerState } from '../../../../api/lending/edge';
import { useGetStateByStateKey } from '../../../../api/lending/lapiHooks';
import { useDebounceEmailAvailability } from '../../../../Buyer/components/EmailInput/useDebounceEmailAvailability';
import { Input } from '../../../../components/Input/Input';
import { useStateKey } from '../../../../hooks/useStateKey.hook';

export const EmailInput = () => {
  const { refetchState: fetchCustomerState } = useGetStateByStateKey();
  const { fetchSiteContext } = useStateKey();
  const [disableEmail, setDisableEmail] = useState(false);
  const [emailIsAvailable, setEmailIsAvailable] = useState<boolean | null>(
    null,
  );
  const debounceEmailAvailability = useDebounceEmailAvailability();

  const handleOnSubmit = async (email: string) => {
    const valid = await handleOnChange({ email: email, errors: {} });
    if (!valid) return;

    try {
      await fetchPostCustomerState({
        data: {
          data: {
            application: {
              people: [
                {
                  emails: [
                    {
                      email,
                      uid: uuidv4(),
                      type: 'primary',
                    },
                  ],
                  uid: uuidv4(),
                  roles: ['applicant'],
                },
              ],
            },
          },
        },
      });

      setDisableEmail(true);

      tracking.ampli.sellerEmailAdded();

      await Promise.all([fetchCustomerState(), fetchSiteContext()]);
    } catch {}
  };

  const handleOnChange = async ({
    email,
    errors,
  }: {
    email: string;
    errors: FormikErrors<{ email?: string }>;
  }) => {
    if ('email' in errors) {
      if (emailIsAvailable) setEmailIsAvailable(null);
      return;
    }

    let isEmailAvailable: null | boolean = null;
    try {
      isEmailAvailable = (await debounceEmailAvailability(email)) as boolean;
    } catch (e: unknown) {}

    if (emailIsAvailable !== isEmailAvailable)
      setEmailIsAvailable(isEmailAvailable);

    return isEmailAvailable;
  };

  return (
    <Formik
      onSubmit={(values) => handleOnSubmit(values.email)}
      initialValues={{ email: '' }}
      validationSchema={validationSchema}
    >
      {({ errors }) => (
        <Form className={styles.emailContainer}>
          <div className={styles.emailGroupWrapper}>
            <Input
              label="Email"
              name="email"
              onChange={(e: BaseSyntheticEvent) =>
                handleOnChange({ email: e.target.value, errors: errors })
              }
              className={styles.input}
            />
            <Button
              variant="secondary"
              type="submit"
              className={styles.topEmailButton}
              disabled={disableEmail || emailIsAvailable === false}
            >
              Save email
            </Button>
          </div>
          {emailIsAvailable === false && (
            <div className={styles.validationError}>
              <p className={styles.validationErrorP}>
                Looks like you already have an account.
              </p>
              <p className={styles.validationErrorP}>
                <a
                  href="/login/?redirect_to=/pay/activation/"
                  className={styles.link}
                >
                  Click here
                </a>{' '}
                to log in
              </p>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email().required(),
});
